import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { APIService } from 'src/api/api';
import { getStoredUserTimezone } from 'src/utils/timezoneUtils';

const DATE_TIME_INPUT_FORMAT = 'YYYY-MM-DDTHH:mm';
const FUEL_EVENT_TYPES = new Set([1, 2, 3, 4]);

const EVENT_LABELS = {
  5: 'Ignition On',
  6: 'Ignition Off',
  14: 'Overspeed start',
  22: 'Switching On',
  23: 'Switching Off',
  30: 'Overspeed end',
  35: 'Entered geozone',
  36: 'Exited geozone',
  42: 'Stop start',
  43: 'Stop end',
  97: 'Parking start',
  98: 'Parking end'
};

const getInitialDateRange = () => {
  const now = moment().tz(getStoredUserTimezone()).seconds(0).milliseconds(0);
  return {
    from: now.clone().startOf('day').format(DATE_TIME_INPUT_FORMAT),
    to: now.format(DATE_TIME_INPUT_FORMAT)
  };
};

const toUnixSeconds = (value, timezoneName) => {
  const parsed = moment.tz(value, DATE_TIME_INPUT_FORMAT, true, timezoneName);
  return parsed.isValid() ? parsed.unix() : null;
};

const normalizeEvents = (response) => {
  const results = response?.result?.results || response?.results || {};
  const rows = Array.isArray(results.rows) ? results.rows : [];
  return rows
    .filter((row) => !FUEL_EVENT_TYPES.has(Number(row?.eventTypeID)))
    .map((row, index) => {
      const displayRow = row.displayRow && typeof row.displayRow === 'object' ? row.displayRow : {};
      const eventType = Number(row.eventTypeID);
      return {
        id: row.id || `general-event-${index + 1}`,
        dateTime: displayRow.dateTime || row.dateTime || '--',
        event: displayRow.event || row.event || EVENT_LABELS[eventType] || `Event ${eventType || '--'}`,
        parameters: displayRow.parameters || row.parameterText || '--',
        address: displayRow.address || row.addressText || row.parseAddress || row.address || '--'
      };
    });
};

const TankGeneralEvents = ({ tank }) => {
  const timezoneName = getStoredUserTimezone();
  const [dateRange, setDateRange] = useState(getInitialDateRange);
  const [appliedRange, setAppliedRange] = useState(getInitialDateRange);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');

  const isEnabled = Boolean(tank?.fuel_graph_enabled);

  const fetchEvents = useCallback(async () => {
    if (!tank?.id || !isEnabled) return;
    const timeBegin = toUnixSeconds(appliedRange.from, timezoneName);
    const timeEnd = toUnixSeconds(appliedRange.to, timezoneName);
    if (timeBegin === null || timeEnd === null || timeBegin >= timeEnd) return;

    setLoading(true);
    setError('');
    try {
      const response = await APIService.getTankGeneralEvents(tank.id, {
        timeBegin,
        timeEnd,
        timezone: timezoneName,
        tz: timezoneName,
        rows: 100,
        page: 1,
        sidx: 'eventDate',
        sord: 'desc'
      });
      if (!response?.success) throw response;
      const normalizedEvents = normalizeEvents(response);
      setEvents(normalizedEvents);
      if (!normalizedEvents.length) setError('No general events found for the selected range.');
    } catch (requestError) {
      setEvents([]);
      setError(requestError?.data?.error_message || requestError?.error_message || 'Unable to load general events.');
    } finally {
      setLoading(false);
    }
  }, [appliedRange, isEnabled, tank?.id, timezoneName]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleDateRangeChange = (field) => (event) => {
    setDateRange((current) => ({ ...current, [field]: event.target.value }));
    setValidationError('');
  };

  const handleApply = () => {
    const timeBegin = toUnixSeconds(dateRange.from, timezoneName);
    const timeEnd = toUnixSeconds(dateRange.to, timezoneName);
    if (timeBegin === null || timeEnd === null) {
      setValidationError('Enter a valid start and end date-time.');
      return;
    }
    if (timeBegin >= timeEnd) {
      setValidationError('End date-time must be after start date-time.');
      return;
    }
    setValidationError('');
    setAppliedRange(dateRange);
  };

  if (!isEnabled) {
    return <Alert severity="info">General events are available after enabling a supported Omnicomm provider and storing the tank unit id.</Alert>;
  }

  let tableBody;
  if (loading) {
    tableBody = (
      <TableRow>
        <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Loading general events</Typography>
        </TableCell>
      </TableRow>
    );
  } else if (events.length) {
    tableBody = events.map((event) => (
      <TableRow key={event.id} hover>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{event.dateTime}</TableCell>
        <TableCell><Chip label={event.event} size="small" color="primary" variant="outlined" sx={{ fontWeight: 700 }} /></TableCell>
        <TableCell>{event.parameters}</TableCell>
        <TableCell sx={{ minWidth: 240 }}>{event.address}</TableCell>
      </TableRow>
    ));
  } else {
    tableBody = (
      <TableRow>
        <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
          <Typography color="text.secondary">No general events found for this range.</Typography>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', lg: 'flex-end' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="h6" fontWeight={800}>General Events</Typography>
          <Typography variant="body2" color="text.secondary">Ignition, equipment, geofence and other Omnicomm device events.</Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-end' }}>
          <TextField label="From" type="datetime-local" value={dateRange.from} onChange={handleDateRangeChange('from')} size="small" InputLabelProps={{ shrink: true }} />
          <TextField label="To" type="datetime-local" value={dateRange.to} onChange={handleDateRangeChange('to')} size="small" InputLabelProps={{ shrink: true }} />
          <Button variant="contained" onClick={handleApply} sx={{ height: 40, textTransform: 'none', fontWeight: 700 }}>Apply</Button>
        </Stack>
      </Stack>

      <Typography variant="caption" color="text.secondary">{`Times shown in ${timezoneName}.`}</Typography>
      {validationError && <Alert severity="warning">{validationError}</Alert>}
      {error && <Alert severity={events.length ? 'warning' : 'error'}>{error}</Alert>}

      <Chip label={`${events.length} event${events.length === 1 ? '' : 's'}`} sx={{ alignSelf: 'flex-start', fontWeight: 700 }} />

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: { xs: 520, md: 620 } }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {['Event time', 'Event', 'Parameters', 'Address'].map((heading) => (
                  <TableCell key={heading} sx={{ bgcolor: '#F8FAFC', fontWeight: 800, whiteSpace: 'nowrap' }}>
                    {heading}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>{tableBody}</TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Stack>
  );
};

TankGeneralEvents.propTypes = {
  tank: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fuel_graph_enabled: PropTypes.bool
  })
};

export default TankGeneralEvents;
